import React, { useEffect, useState } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
    Button, Textarea, Table, Thead, Tbody, Tr, Th, Td, useToast
} from '@chakra-ui/react';
import axios from 'axios';
import Swal from 'sweetalert2';

const CommentModal = ({ isOpen, onClose, lessonID, user, userRole }) => {
    const [comment, setComment] = useState('');
    const [comments, setComments] = useState([]);
    const [editingId, setEditingId] = useState(null); // 🔥 NEW
    const toast = useToast();

    useEffect(() => {
        if (isOpen && lessonID) {
            loadComments();
        }
    }, [isOpen, lessonID]);

    // ---------------- SAFE USER ----------------
    const getSafeUser = () => {
        try {
            if (!user) return null;
            if (typeof user === "string") return JSON.parse(user);
            return user;
        } catch (e) {
            console.error("Invalid user JSON:", e);
            return null;
        }
    };

    const parsedUser = getSafeUser();

    const isTeacher = userRole && userRole === "TEACHER";

    console.log(`role: ${userRole}`)

    // ---------------- LOAD ----------------
    const loadComments = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/comment/list`,
                {
                    params: { lesson_id: lessonID },
                    withCredentials: true
                }
            );

            setComments(res.data.result || []);
        } catch (err) {
            console.error(err);
            toast({
                title: "โหลด comment ไม่สำเร็จ",
                status: "error",
                duration: 2000
            });
        }
    };

    // ---------------- FORMAT DATE ----------------
    const formatDate = (dateStr) => {
        if (!dateStr) return "-";

        const date = new Date(dateStr);
        if (isNaN(date)) return dateStr;

        return date.toLocaleString("th-TH", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        });
    };

    // ---------------- SUBMIT (CREATE / UPDATE) ----------------
    const handleSubmit = async () => {
        if (!comment || comment.trim() === '') return;

        try {
            let res;

            if (editingId) {
                // 🔥 UPDATE MODE
                res = await axios.post(
                    `${process.env.REACT_APP_API_URL}/comment/update`,
                    {
                        comment_id: editingId,
                        comment: comment
                    },
                    { withCredentials: true }
                );
            } else {
                // CREATE MODE
                res = await axios.post(
                    `${process.env.REACT_APP_API_URL}/comment/create`,
                    {
                        lesson_id: lessonID,
                        comment: comment
                    },
                    { withCredentials: true }
                );
            }

            if (res.data.status === "success") {
                setComment('');
                setEditingId(null);
                loadComments();

                toast({
                    title: editingId ? "แก้ไขสำเร็จ" : "เพิ่ม comment สำเร็จ",
                    status: "success",
                    duration: 2000
                });
            } else {
                toast({
                    title: "ไม่สำเร็จ",
                    status: "error",
                    duration: 2000
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    // ---------------- EDIT ----------------
    const handleEdit = (c) => {
        setComment(c.comment);
        setEditingId(c.id);
    };

    // ---------------- CANCEL EDIT ----------------
    const handleCancelEdit = () => {
        setComment('');
        setEditingId(null);
    };

    // ---------------- DELETE ----------------
    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "ยืนยันลบ?",
            text: "คุณต้องการลบ comment นี้ใช่หรือไม่",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#e53e3e",
            cancelButtonColor: "#718096",
            confirmButtonText: "ลบ",
            cancelButtonText: "ยกเลิก"
        });

        if (!result.isConfirmed) return;

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/comment/delete`,
                { comment_id: id },
                { withCredentials: true }
            );

            if (res.data.status === "success") {
                loadComments();
                toast({
                    title: "ลบ comment สำเร็จ",
                    status: "success",
                    duration: 2000
                });
            } else {
                toast({
                    title: "ไม่สามารถลบได้",
                    status: "error",
                    duration: 2000
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="5xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    {editingId ? `แก้ไข Comment (ID:${editingId})` : !isTeacher ? "เพิ่ม Comment" : "Comment"}
                </ModalHeader>

                <ModalBody>
                    {!isTeacher && (
                        <Textarea
                            placeholder="พิมพ์ comment..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            mb={4}
                        />
                    )}

                    <Table size="sm">
                        <Thead>
                            <Tr>
                                <Th>No.</Th>
                                <Th>Comment</Th>
                                <Th>Comment by</Th>
                                <Th>Datetime</Th>
                                {!isTeacher && <Th>Action</Th>}
                            </Tr>
                        </Thead>

                        <Tbody>
                            {comments.map((c, i) => {
                                const canEditOrDelete =
                                    Number(parsedUser?.id) === Number(c.strapi_administrator_id) ||
                                    (parsedUser?.role &&
                                        parsedUser.role === "SUPER_ADMIN");

                                return (
                                    <Tr key={c.id || i}>
                                        <Td>{i + 1}</Td>
                                        <Td>{c.comment}</Td>
                                        <Td>{c.email}</Td>
                                        <Td>{formatDate(c.created_at)}</Td>
                                        {!isTeacher && (
                                            <Td>
                                                {canEditOrDelete && (
                                                    <>
                                                        <Button
                                                            size="xs"
                                                            mr={2}
                                                            colorScheme="yellow"
                                                            onClick={() => handleEdit(c)}
                                                        >
                                                            แก้ไข
                                                        </Button>

                                                        <Button
                                                            size="xs"
                                                            colorScheme="red"
                                                            onClick={() => handleDelete(c.id)}
                                                        >
                                                            ลบ
                                                        </Button>
                                                    </>
                                                )}
                                            </Td>)}
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </ModalBody>

                <ModalFooter>
                    {editingId && !isTeacher && (
                        <Button mr={3} onClick={handleCancelEdit}>
                            ยกเลิกแก้ไข
                        </Button>
                    )}

                    <Button mr={3} onClick={onClose}>
                        ปิด
                    </Button>

                    {!isTeacher && (
                        <Button
                            colorScheme="blue"
                            onClick={handleSubmit}
                            isDisabled={!comment || comment.trim() === ""}
                        >
                            {editingId ? "อัปเดต" : "บันทึก"}
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CommentModal;